<template>
  <div class="map-tooltip">
    <div
      v-if="singleMarker"
      class="map-tooltip__card">
      <img
        v-if="singleMarker.logoUrl"
        class="map-tooltip__logo"
        :src="singleMarker.logoUrl"
        alt=""
        @error="hideBrokenLogo" />
      <div class="map-tooltip__body">
        <span class="map-tooltip__name">{{ singleMarker.name }}</span>
        <span
          v-if="singleMarker.subtitle"
          class="map-tooltip__row">
          <img
            class="map-tooltip__icon"
            :src="ROW_ICONS.subtitle"
            alt="" />
          <span class="map-tooltip__row-text">{{ singleMarker.subtitle }}</span>
        </span>
        <span
          v-if="singleMarker.detail"
          class="map-tooltip__row">
          <img
            class="map-tooltip__icon"
            :src="ROW_ICONS.detail"
            alt="" />
          <span class="map-tooltip__row-text">{{ singleMarker.detail }}</span>
        </span>
        <span
          v-if="singleMarker.location"
          class="map-tooltip__row">
          <img
            class="map-tooltip__icon"
            :src="ROW_ICONS.location"
            alt="" />
          <span class="map-tooltip__row-text">{{ singleMarker.location }}</span>
        </span>
      </div>
    </div>

    <template v-else>
      <ul class="map-tooltip__list">
        <li
          v-for="marker in visibleMarkers"
          :key="marker.id"
          class="map-tooltip__list-item">
          {{ marker.name }}
        </li>
      </ul>
      <span
        v-if="hiddenCount > 0"
        class="map-tooltip__more"
        >{{ formatMoreCount(hiddenCount) }}</span
      >
    </template>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import type { ComputedRef, PropType } from 'vue'

import type { EstablishmentMapTooltipProps } from '~/types/EstablishmentMapTooltip'
import type { EstablishmentSearchMapMarker } from '~/types/EstablishmentSearchMap'

/** Au-delà, la liste cluster est tronquée et affiche « et N de plus ». */
const MAX_CLUSTER_ITEMS: number = 5

/** Icônes des cartes de la liste, réutilisées pour le même langage visuel. */
const ROW_ICONS: { subtitle: string; detail: string; location: string } = {
  subtitle: '/icons/school-card/icon-sales.svg',
  detail: '/icons/school-card/icon-diploma.svg',
  location: '/icons/school-card/icon-location-pin.svg',
}

/**
 * Définit les props du composant EstablishmentMapTooltip.
 */
const props: EstablishmentMapTooltipProps = defineProps({
  markers: {
    type: Array as PropType<EstablishmentSearchMapMarker[]>,
    required: true,
  },
  formatMoreCount: {
    type: Function as PropType<(count: number) => string>,
    required: true,
  },
})

/** Entités uniques au point (une école multi-campus produit plusieurs marqueurs de même id). */
const uniqueMarkers: ComputedRef<EstablishmentSearchMapMarker[]> = computed(
  (): EstablishmentSearchMapMarker[] => {
    const seenIds: Set<string> = new Set<string>()
    const result: EstablishmentSearchMapMarker[] = []
    for (const marker of props.markers) {
      if (seenIds.has(marker.id)) continue
      seenIds.add(marker.id)
      result.push(marker)
    }
    return result
  },
)

/** Une seule entité au point → mini-carte détaillée ; plusieurs → liste. */
const singleMarker: ComputedRef<EstablishmentSearchMapMarker | null> = computed(
  (): EstablishmentSearchMapMarker | null =>
    uniqueMarkers.value.length === 1 ? uniqueMarkers.value[0]! : null,
)

const visibleMarkers: ComputedRef<EstablishmentSearchMapMarker[]> = computed(
  (): EstablishmentSearchMapMarker[] => uniqueMarkers.value.slice(0, MAX_CLUSTER_ITEMS),
)

const hiddenCount: ComputedRef<number> = computed(
  (): number => uniqueMarkers.value.length - visibleMarkers.value.length,
)

/**
 * Masque le logo si l'image ne charge pas (évite une vignette cassée).
 * @param {Event} event - Événement `error` de l'image.
 * @returns {void}
 */
function hideBrokenLogo(event: Event): void {
  if (event.target instanceof HTMLElement) event.target.style.display = 'none'
}
</script>

<style scoped>
.map-tooltip {
  width: max-content;
  max-width: 240px;
  box-sizing: border-box;
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--border-default);
  border-left: 3px solid var(--accent-orange);
  border-radius: var(--radius-md);
  background: linear-gradient(135deg, var(--bg-surface) 0%, var(--bg-elevated) 100%);
  box-shadow: var(--shadow-lg);
  color: var(--text-primary);
  font-family: var(--font-body);
}

.map-tooltip__card {
  display: flex;
  gap: var(--space-2);
  align-items: flex-start;
}

.map-tooltip__logo {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  padding: 3px;
  box-sizing: border-box;
  border-radius: var(--radius-md);
  background: #fff;
  object-fit: contain;
}

.map-tooltip__body {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.map-tooltip__name {
  font-size: var(--text-sm);
  font-weight: 700;
  color: var(--text-primary);
  line-height: var(--leading-snug);
  overflow-wrap: break-word;
}

.map-tooltip__row {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  min-width: 0;
  font-size: var(--text-xs);
  color: var(--text-secondary);
  line-height: var(--leading-snug);
}

.map-tooltip__icon {
  flex-shrink: 0;
  width: 11px;
  height: 11px;
  object-fit: contain;
}

.map-tooltip__row-text {
  min-width: 0;
  overflow-wrap: break-word;
}

.map-tooltip__list {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  margin: 0;
  padding: 0;
  list-style: none;
}

.map-tooltip__list-item {
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--text-primary);
  line-height: var(--leading-snug);
  overflow-wrap: break-word;
}

.map-tooltip__more {
  display: block;
  margin-top: var(--space-1);
  font-size: var(--text-xs);
  font-style: italic;
  color: var(--text-secondary);
}
</style>
